import { useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Input,
  Link,
} from '@optiaxiom/react';
import { IconChevronDown, IconSearch, IconXmark } from '@optimizely/axiom-icons';

import { PageHeader } from '@frontend/components';
import { AppPagePadding } from '@frontend/styles';

import { useRAGConfiguration } from '../hooks/useRAGConfigurations';

import { styles } from './RAGConfiguration.css';
import RAGConfigurationTable from './RAGConfigurationTable';
import RAGProvisionConfiguration from './RAGProvisionConfiguration';

function RAGConfigurationList() {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const { configurations, isLoadingConfigurations, toggleStatus } = useRAGConfiguration();

  // Predefined product types
  const productTypes = ['Experimentation', 'Content Graph', 'Content Marketing Platform'];

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  // Filter configurations based on search and filters
  const filteredConfigurations = useMemo(() => {
    return configurations.filter(config => {
      // Text filter
      if (filterText) {
        const lowerCaseFilter = filterText.toLowerCase();
        const textMatch =
          config.opal_instance_id.toLowerCase().includes(lowerCaseFilter) ||
          config.product.toLowerCase().includes(lowerCaseFilter) ||
          config.spanner_db_name.toLowerCase().includes(lowerCaseFilter);
        if (!textMatch) return false;
      }

      // Product filter
      if (productFilter.length > 0) {
        if (!productFilter.includes(config.product)) return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = config.deactivated ? 'inactive' : 'active';
        if (!statusFilter.includes(status)) return false;
      }

      return true;
    });
  }, [configurations, filterText, productFilter, statusFilter]);

  // Filter functions
  const handleProductSelect = (product: string) => {
    const newProducts = productFilter.includes(product)
      ? productFilter.filter(p => p !== product)
      : [...productFilter, product];
    setProductFilter(newProducts);
  };

  const handleProductSelectAll = () => {
    const allSelected = productTypes.every(product => productFilter.includes(product));
    if (allSelected) {
      setProductFilter([]);
    } else {
      setProductFilter([...productTypes]);
    }
  };

  const handleStatusSelect = (status: string) => {
    const newStatuses = statusFilter.includes(status)
      ? statusFilter.filter(s => s !== status)
      : [...statusFilter, status];
    setStatusFilter(newStatuses);
  };

  const handleStatusSelectAll = () => {
    const allSelected = statusOptions.every(status => statusFilter.includes(status.value));
    if (allSelected) {
      setStatusFilter([]);
    } else {
      setStatusFilter(statusOptions.map(status => status.value));
    }
  };

  const getProductFilterLabel = () => {
    if (productFilter.length === 0) return 'All Products';
    if (productFilter.length === productTypes.length) return 'All Products';
    if (productFilter.length === 1) return productFilter[0];
    return `${productFilter.length} Products`;
  };

  const getStatusFilterLabel = () => {
    if (statusFilter.length === 0) return 'All Status';
    if (statusFilter.length === statusOptions.length) return 'All Status';
    if (statusFilter.length === 1) return statusFilter[0] === 'active' ? 'Active' : 'Inactive';
    return 'Both Status';
  };

  const handleProvision = () => {
    navigate('/rag/provision');
  };

  const handleToggleStatus = (turnstileId: string) => {
    toggleStatus(turnstileId);
  };

  return (
    <Flex flexDirection="column" w="full" h="full">
      <Flex className={AppPagePadding} w="full">
        <PageHeader
          title="RAG Configuration"
          headingText={
            <>
              Enhance Opal&apos;s output leveraging retrieval-augmented generation.{' '}
              <Link
                external
                href="https://support.optimizely.com/hc/en-us/sections/40604731575181-Retrieval-Augmented-Generation-RAG"
              >
                Learn more.
              </Link>
            </>
          }
        />
      </Flex>

      <Flex flex="1" className={AppPagePadding} pt="0" flexDirection="column" gap="24">
        {/* Search and Filter Section */}
        <Flex className={styles.searchInputContainer}>
          <Flex flexDirection="row" gap="8" alignItems="center">
            <Input
              className={styles.searchInput}
              addonBefore={<IconSearch />}
              addonAfter={filterText && <IconXmark onClick={() => setFilterText('')} />}
              placeholder="Search configurations..."
              value={filterText}
              onValueChange={setFilterText}
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button appearance="default" addonAfter={<IconChevronDown size="16" />}>
                  {getProductFilterLabel()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleProductSelectAll}>
                  <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    w="full"
                  >
                    All Products
                    {productTypes.every(product => productFilter.includes(product)) && (
                      <Badge variant="subtle" intent="information">
                        ✓
                      </Badge>
                    )}
                  </Flex>
                </DropdownMenuItem>
                {productTypes.map(product => (
                  <DropdownMenuItem key={product} onClick={() => handleProductSelect(product)}>
                    <Flex
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                      w="full"
                    >
                      {product}
                      {productFilter.includes(product) && (
                        <Badge variant="subtle" intent="information">
                          ✓
                        </Badge>
                      )}
                    </Flex>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button appearance="default" addonAfter={<IconChevronDown size="16" />}>
                  {getStatusFilterLabel()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleStatusSelectAll}>
                  <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    w="full"
                  >
                    All Status
                    {statusOptions.every(status => statusFilter.includes(status.value)) && (
                      <Badge variant="subtle" intent="information">
                        ✓
                      </Badge>
                    )}
                  </Flex>
                </DropdownMenuItem>
                {statusOptions.map(status => (
                  <DropdownMenuItem
                    key={status.value}
                    onClick={() => handleStatusSelect(status.value)}
                  >
                    <Flex
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                      w="full"
                    >
                      {status.label}
                      {statusFilter.includes(status.value) && (
                        <Badge variant="subtle" intent="information">
                          ✓
                        </Badge>
                      )}
                    </Flex>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </Flex>

          <Button appearance="primary" size="md" onClick={handleProvision}>
            Provision Configuration
          </Button>
        </Flex>

        <RAGConfigurationTable
          configurations={filteredConfigurations}
          isLoadingConfigurations={isLoadingConfigurations}
          onToggleStatus={handleToggleStatus}
        />
      </Flex>
    </Flex>
  );
}

function RAGConfiguration() {
  return (
    <Routes>
      <Route path="/provision" element={<RAGProvisionConfiguration />} />
      <Route path="/*" element={<RAGConfigurationList />} />
    </Routes>
  );
}

export default RAGConfiguration;
